import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import {
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  LOCALES,
  isLocale,
  type Locale,
} from '@arriba.digital/contracts';
import { setRequestLocale } from '../api/client';
import en from './locales/en.json';
import es from './locales/es.json';
import ru from './locales/ru.json';

/**
 * Internationalisation.
 *
 * Adding a language is three steps: append the code to `LOCALES` in
 * `@arriba.digital/contracts`, drop a JSON bundle in `locales/`, and register it in
 * `resources` below. Nothing else in the codebase hard-codes a language, and
 * `LOCALE_LABELS` in the contracts package supplies the display name so the
 * switcher builds itself.
 */

export const resources = {
  en: { translation: en },
  es: { translation: es },
  ru: { translation: ru },
} as const;

const STORAGE_KEY = 'arriba.digital.locale';

/** Stored choice, else the browser's preference, else the product default. */
export function resolveInitialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) {
      return stored;
    }
  } catch {
    /* storage may be blocked */
  }

  for (const candidate of navigator.languages ?? [navigator.language]) {
    const base = candidate.split('-')[0]?.toLowerCase();
    if (isLocale(base)) {
      return base;
    }
  }

  return DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: resolveInitialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  supportedLngs: [...LOCALES],
  defaultNS: 'translation',
  interpolation: {
    // React already escapes everything it renders.
    escapeValue: false,
  },
  returnNull: false,
});

/** Keep the API's response language in step with the interface language. */
setRequestLocale(i18n.language as Locale);

/**
 * The workspace's own name, once a session says what it is.
 *
 * The tab title names the product by default (`app.name`), but a company that
 * has chosen *Settings → Organisation → Display name* expects to see itself —
 * and the title is rewritten on every language change, so the override is held
 * here rather than assigned from a component that i18n would then clobber.
 */
let companyName: string | null = null;

/**
 * Point the document title at the company, or clear it with `null` on sign-out
 * so the public pages go back to naming the product.
 */
export function setDocumentCompanyName(name: string | null): void {
  companyName = name && name.trim() !== '' ? name.trim() : null;
  applyDocumentMetadata(currentLocale());
}

/**
 * Reflect the active language in the document itself: `<html lang>`, the tab
 * title and the meta description. Without this a Spanish visitor would keep
 * the static English `<title>` baked into `index.html` for the whole session.
 */
function applyDocumentMetadata(locale: Locale): void {
  document.documentElement.lang = locale;
  document.title = `${companyName ?? i18n.t('app.name')} — ${i18n.t('app.tagline')}`;

  const description = document.querySelector('meta[name="description"]');
  if (description) {
    description.setAttribute('content', i18n.t('app.tagline'));
  }
}

i18n.on('languageChanged', (language) => {
  const locale = (isLocale(language) ? language : DEFAULT_LOCALE) as Locale;

  setRequestLocale(locale);
  applyDocumentMetadata(locale);

  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
});

applyDocumentMetadata(currentLocale());

/** Switch language everywhere: interface, `<html lang>` and API responses. */
export async function changeLocale(locale: Locale): Promise<void> {
  await i18n.changeLanguage(locale);
}

/** Current locale as a contract type rather than a free-form string. */
export function currentLocale(): Locale {
  return (isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE) as Locale;
}

export default i18n;
